

import React, { useState, useEffect, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { API_URL } from '../../../utils/constant';
import { fetchSuppliers } from '../../../slices/company/supplierSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { customTableStyles } from '../../../utils/tableStylings';

const SalesReport = () => {

    const formatLocalDate = date => {
        const d = date.getDate().toString().padStart(2, '0');
        const m = (date.getMonth() + 1).toString().padStart(2, '0'); // month is 0-based
        const y = date.getFullYear();
        return `${y}-${m}-${d}`;
    };

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Format as yyyy-mm-dd for input[type="date"]
    const [filters, setFilters] = useState({
        invoice: '',
        type: 'All',
        party: '',
        article: '',
        month: '',
        segment: '',
        size: '',
        colour: '',
        dateFrom: formatLocalDate(firstDayOfMonth),
        dateTo: formatLocalDate(today)
    });

    const [pendingFilters, setPendingFilters] = useState({
        invoice: '',
        type: 'All',
        party: '',
        article: '',
        month: '',
        segment: '',
        size: '',
        colour: '',
        dateFrom: formatLocalDate(firstDayOfMonth),
        dateTo: formatLocalDate(today)
    });

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [salesExecutives, setSalesExecutives] = useState([]);
    const [selectedExecutive, setSelectedExecutive] = useState('');
    const { suppliers = [] } = useSelector((state) => state.suppliers);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [colourOptions, setColourOptions] = useState([]);
    const [articleOptions, setArticleOptions] = useState([]);
    const dispatch = useDispatch();
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        fetchData();
    }, [filters]);

    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            dispatch(fetchSuppliers(company.company?.id));
            fetchSalesExecutives();
        }

    }, []);

    const fetchSalesExecutives = async () => {
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company?.company?.id;
            if (!companyId) return;
            const res = await axios.get(`${API_URL}/companies/sales/executives/${companyId}`);
            console.log('Sales Executives:', res);
            if (res.status == 200) {
                setSalesExecutives(res.data);
            }
        } catch (err) {
            console.error('Error fetching sales executives:', err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setData([]);
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company?.company?.id;
            if (!companyId) {
                setError('Company not found');
                return;
            }

            const queryParams = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) queryParams.append(key, filters[key]);
            });

            const response = await axios.get(`${API_URL}/pos/sales-report/${companyId}?${queryParams.toString()}`);
            if (response.data.success) {
                setData(response.data.data);
                // Update dynamic options
                const uniqueSegments = [...new Set(response.data.data.map(row => row.segment).filter(Boolean))];
                const uniqueSizes = [...new Set(response.data.data.map(row => row.size).filter(Boolean))];
                const uniqueColours = [...new Set(response.data.data.map(row => row.colour).filter(Boolean))];
                const uniqueArticles = [...new Set(response.data.data.map(row => row.articleNo).filter(Boolean))];
                setSegmentOptions(uniqueSegments);
                setSizeOptions(uniqueSizes);
                setColourOptions(uniqueColours);
                setArticleOptions(uniqueArticles);
            } else {
                setError('Failed to fetch data');
            }
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handlePendingFilterChange = (key, value) => {
        setPendingFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        setFilters(pendingFilters);
    };

    const resetFilters = () => {
        const emptyFilters = {
            invoice: '',
            party: '',
            article: '',
            month: '',
            segment: '',
            size: '',
            colour: '',
            dateFrom: '',
            dateTo: ''
        };
        setPendingFilters(emptyFilters);
        setFilters(emptyFilters);
    };

    const handleReassign = (row) => {
        setSelectedRow(row);
        setSelectedExecutive(row.salesExecutive || '');
        setShowModal(true);
    };

    const handleUpdateExecutive = async () => {
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company?.company?.id;
            await axios.put(`${API_URL}/pos/update-sales-executive/${companyId}`, {
                sales_bill_id: selectedRow.invoice,
                sales_executive_id: selectedExecutive,
                companyId
            });
            setShowModal(false);
            fetchData(); // Refresh data
        } catch (err) {
            console.error('Error updating sales executive:', err);
            setError('Failed to update sales executive');
        }
    };

    const exportToExcel = () => {
        const workbook = XLSX.utils.book_new();

        const summaryWorksheet = XLSX.utils.json_to_sheet(salesSummary.map((exec, index) => ({
            '#': index + 1,
            'Sales Executive': exec.name,
            'Total Sales': exec.amount,
            'Items Sold': exec.qty
        })));
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Sales Executive Summary');
        // Main sales data sheet
        const mainWorksheet = XLSX.utils.json_to_sheet(data.map((row, index) => ({
            '#': index + 1,
            'Invoice': row.invoice,
            'Vendor': row.vendor,
            'Article No': row.articleNo,
            'Segment': row.segment,
            'Sub Segment': row.subSegment,
            'Variant': row.variant,
            'Serial Nos': row.serialNos,
            'Date': row.date,
            'Total Amount': row.totalAmount,
            'Sales Executive': row.salesExecutive || 'Unknown'
        })));
        XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Sales Report');

        // Sales Executive Summary sheet


        XLSX.writeFile(workbook, 'sales_report.xlsx');
    };

    const exportToPDF = () => {
        const doc = new jsPDF('landscape');

        // Add title
        doc.setFontSize(16);
        doc.text('Sales Report', 14, 20);

        // Add filters info if any are applied
        let yPosition = 30;
        const activeFilters = Object.entries(filters).filter(([key, value]) => value);
        if (activeFilters.length > 0) {
            doc.setFontSize(10);
            doc.text('Applied Filters:', 14, yPosition);
            yPosition += 5;
            activeFilters.forEach(([key, value]) => {
                doc.text(`${key}: ${value}`, 14, yPosition);
                yPosition += 5;
            });
            yPosition += 5;
        }

        const summaryTableData = salesSummary.map((exec, index) => [
            index + 1,
            exec.name,
            exec.amount.toFixed(2),
            exec.qty
        ]);

        autoTable(doc, {
            head: [['#', 'Sales Executive', 'Total Sales', 'Items Sold']],
            body: summaryTableData,
            startY: yPosition,
            styles: {
                fontSize: 10,
                cellPadding: 3
            },
            headStyles: {
                fillColor: [52, 152, 219],
                textColor: 255,
                fontSize: 11,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            margin: { top: 20 }
        });

        // Prepare table data
        const tableData = data.map((row, index) => [
            index + 1,
            row.invoice,
            row.articleNo,
            row.segment,
            row.subSegment,
            row.variant,
            row.serialNos,
            row.date,
            row.totalAmount,
            row.salesExecutive || 'Unknown'
        ]);

        // Add table
        autoTable(doc, {
            head: [['#', 'Invoice', 'Article No', 'Segment', 'Sub Segment', 'Variant', 'Serial Nos', 'Date', 'Total Amount', 'Sales Executive']],
            body: tableData,

            styles: {
                fontSize: 8,
                cellPadding: 2
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontSize: 9,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            margin: { top: 10 }
        });

        // Add Sales Executive Summary table


        doc.save('sales_report.pdf');
    };

    const salesSummary = useMemo(() => {
        const summary = {};
        data.forEach(row => {
            const exec = row.salesExecutive || 'Unknown';
            if (!summary[exec]) summary[exec] = { amount: 0, qty: 0 };
            summary[exec]['amount'] += parseFloat(row.totalAmount) || 0;
            summary[exec]['qty'] += 1;
        });

        // Include all sales executives with 0 if no data
        const allExecutives = salesExecutives.map(exec => ({
            name: exec.name,
            amount: summary[exec.name]?.amount || 0,
            qty: summary[exec.name]?.qty || 0
        }));

        // Add Unknown if it exists
        if (summary['Unknown']) {
            allExecutives.push({
                name: 'Unknown',
                amount: summary['Unknown'].amount,
                qty: summary['Unknown'].qty
            });
        }

        return allExecutives;
    }, [data, salesExecutives]);

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "50px",


        },
        {
            name: 'Invoice',
            selector: row => row.invoice,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Vendor',
            selector: row => row.vendor,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Article no',
            selector: row => row.articleNo,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Segment',
            selector: row => row.segment,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'sub segment',
            selector: row => row.subSegment,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'variant',
            selector: row => row.variant,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'serial_no\'s',
            selector: row => row.serialNos,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'date',
            selector: row => row.date,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'total amount',
            selector: row => row.totalAmount,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Sales Executive',
            selector: row => row.salesExecutive !== '' ? row.salesExecutive : 'Unknown',
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Actions',
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleReassign(row)}
                >
                    Reassign
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "120px"
        }
    ];

    return (
        <div className="card">
            <div className='card-header'>
                <h3 className="text-primary mb-0">Sales Report</h3>
            </div>
            <div className="card-body">
                <h5 className="card-title">Filters</h5>
                <div className="row g-3 mb-3">
                    <div className="col-md-3">
                        <select className="form-control" onChange={e => handlePendingFilterChange('type', e.target.value)}>
                            <option>All</option>
                            <option value="0">Existing Products</option>
                            <option value="1">Old Products</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <input
                            type="text"
                            placeholder="Invoice wise"
                            value={pendingFilters.invoice}
                            onChange={e => handlePendingFilterChange('invoice', e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.party}
                            onChange={e => handlePendingFilterChange('party', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Suppliers</option>
                            {suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.article}
                            onChange={e => handlePendingFilterChange('article', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Article wise</option>
                            {articleOptions.map(article => (
                                <option key={article} value={article}>{article}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.month}
                            onChange={e => handlePendingFilterChange('month', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Month wise</option>
                            {monthNames.map((name, index) => (
                                <option key={index + 1} value={index + 1}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.segment}
                            onChange={e => handlePendingFilterChange('segment', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Segment wise</option>
                            {segmentOptions.map(segment => (
                                <option key={segment} value={segment}>{segment}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.size}
                            onChange={e => handlePendingFilterChange('size', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Size wise</option>
                            {sizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.colour}
                            onChange={e => handlePendingFilterChange('colour', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Colour wise</option>
                            {colourOptions.map(colour => (
                                <option key={colour} value={colour}>{colour}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <input
                            type="date"
                            placeholder="Date from"
                            value={pendingFilters.dateFrom}
                            onChange={e => handlePendingFilterChange('dateFrom', e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            type="date"
                            placeholder="Date to"
                            value={pendingFilters.dateTo}
                            onChange={e => handlePendingFilterChange('dateTo', e.target.value)}
                            className="form-control"
                        />
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button
                        onClick={applyFilters}
                        className="btn btn-primary"
                    >
                        Apply
                    </button>
                    <button
                        onClick={resetFilters}
                        className="btn btn-secondary"
                    >
                        Reset
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="btn btn-success"
                        disabled={data.length === 0}
                    >
                        Export Excel
                    </button>
                    <button
                        onClick={exportToPDF}
                        className="btn btn-danger"
                        disabled={data.length === 0}
                    >
                        Export PDF
                    </button>
                </div>
            </div>
            <div className='card-body'>
                <div className="mb-0">
                    <h5 className="card-title">Sales Executives Summary</h5>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="card bg-secondary">
                                <div className="card-body">
                                    <h6 className='text-white'>Total Sales</h6>
                                    <p className="card-text text-white mb-0">Sales Value: ₹{data.reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0).toFixed(2)}</p>
                                    <p className="card-text text-white">{data.length ?? 0} Items</p>
                                </div>
                            </div>
                        </div>
                        {salesSummary.map((exec, index) => (
                            <div key={index} className="col-md-3">
                                <div className="card">
                                    <div className="card-body">
                                        <h6 >{exec.name}</h6>
                                        <p className="card-text mb-0">Sales Value: ₹{exec.amount.toFixed(2)}</p>
                                        <p className="card-text">{exec.qty > 1 ? `${exec.qty} Items` : `${exec.qty ?? 0} Item`}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {loading && <div className="alert alert-info mb-4">Loading...</div>}
                {error && <div className="alert alert-danger mb-4">Error: {error}</div>}
                {!loading && (<DataTable
                    columns={columns}
                    data={data}
                    pagination
                    highlightOnHover
                    customStyles={customTableStyles}
                    striped
                />)}
            </div>


            {/* Reassign Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Reassign Sales Executive</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-3 text-danger">Reassign the sales executive for this specific bill. This change applies at the bill level, not to individual items in the table below.</p>
                                <p><strong>Invoice:</strong> {selectedRow?.invoice}</p>
                                <div className="mb-3">
                                    <label className="form-label">Select Sales Executive</label>
                                    <select
                                        className="form-select"
                                        value={selectedExecutive}
                                        onChange={(e) => setSelectedExecutive(e.target.value)}
                                    >
                                        <option value="">Select Sales Executive</option>
                                        {salesExecutives.map(exec => (
                                            <option key={exec.id} value={exec.id}>{exec.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleUpdateExecutive}>Update</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showModal && <div className="modal-backdrop fade show"></div>}
        </div>
    );
};

export default SalesReport;